import React, {
  useEffect,
  useState,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../../store/store';

import {
  AdminUsersQuery,
  AdminUsersStatusFilter,
  fetchAdminUsersRequest,
} from '../../store/slice/adminUsersSlice';

function formatDateTime(
  value?: string | null,
) {
  if (!value) {
    return '-';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }

  return date.toLocaleString(
    'ko-KR',
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
  );
}

function statusLabel(
  value?: string | null,
) {
  const normalized =
    String(
      value ?? '',
    )
      .trim()
      .toLowerCase();

  if (
    normalized === 'active'
  ) {
    return '활성';
  }

  if (
    normalized === 'past_due'
  ) {
    return '결제 이슈';
  }

  if (
    normalized === 'canceled' ||
    normalized === 'cancelled'
  ) {
    return '해지';
  }

  if (
    normalized === 'free' ||
    !normalized
  ) {
    return '미구독';
  }

  return normalized;
}

function statusTone(
  value?: string | null,
):
  | 'ok'
  | 'warn'
  | 'danger'
  | 'neutral' {
  const normalized =
    String(
      value ?? '',
    )
      .trim()
      .toLowerCase();

  if (
    normalized === 'active'
  ) {
    return 'ok';
  }

  if (
    normalized === 'past_due'
  ) {
    return 'warn';
  }

  if (
    normalized === 'canceled' ||
    normalized === 'cancelled'
  ) {
    return 'danger';
  }

  return 'neutral';
}

const ADMIN_USERS_SEARCH_DEBOUNCE_MS = 150;

export default function AdminUsersGrid() {
  const dispatch =
    useDispatch();

  const {
    items,
    total,
    pageCount,
    loading,
    loaded,
    error,
    query,
  } = useSelector(
    (
      state: RootState,
    ) => state.adminUsers,
  );

  /*
   * qInput만 local state.
   *
   * 이유:
   * 키 입력을 150ms debounce하기 위한 임시 값일 뿐,
   * 실제 조회 조건은 Redux query가 authority.
   */
  const [
    qInput,
    setQInput,
  ] = useState(
    query.q,
  );

  /*
   * 최초 화면 진입.
   *
   * loaded=false인 경우에만
   * 최초 1회 요청.
   */
  useEffect(() => {
    if (
      loaded ||
      loading
    ) {
      return;
    }

    dispatch(
      fetchAdminUsersRequest(
        query,
      ),
    );
  }, [
    dispatch,
    loaded,
    loading,
    query,
  ]);

  /*
   * 검색어 debounce.
   */
  useEffect(() => {
    const timer =
      window.setTimeout(
        () => {
          const nextQ =
            qInput.trim();

          if (
            nextQ ===
            query.q
          ) {
            return;
          }

          dispatch(
            fetchAdminUsersRequest(
              {
                ...query,

                q: nextQ,

                /*
                 * 검색조건 변경 시
                 * 항상 첫 페이지부터.
                 */
                page: 1,
              },
            ),
          );
        },
        ADMIN_USERS_SEARCH_DEBOUNCE_MS,
      );

    return () => {
      window.clearTimeout(
        timer,
      );
    };
  }, [
    dispatch,
    qInput,
    query,
  ]);

  const request = (
    next:
      Partial<
        AdminUsersQuery
      >,
  ) => {
    dispatch(
      fetchAdminUsersRequest(
        {
          ...query,
          ...next,
        },
      ),
    );
  };

  const submitSearch = () => {
    const nextQ =
      qInput.trim();

    /*
     * Enter 검색은 debounce를 기다리지 않는다.
     *
     * 같은 검색어 + 1페이지라면 불필요한 중복 요청을 만들지 않는다.
     * 같은 검색어라도 다른 페이지에 있다면 검색의 시작점인 1페이지로 이동한다.
     */
    if (
      nextQ === query.q &&
      query.page === 1
    ) {
      return;
    }

    dispatch(
      fetchAdminUsersRequest(
        {
          ...query,
          q: nextQ,
          page: 1,
        },
      ),
    );
  };

  const refresh = () => {
    dispatch(
      fetchAdminUsersRequest(
        query,
      ),
    );
  };

  const page =
    query.page;

  const size =
    query.size;

  return (
    <section
      style={{
        border:
          '1px solid #d8e2eb',

        borderRadius:
          16,

        background:
          '#fff',

        overflow:
          'hidden',

        boxShadow:
          '0 10px 30px rgba(17,39,61,.055)',
      }}
    >
      <header
        style={{
          padding:
            '18px 20px',

          background:
            'linear-gradient(135deg,#08314f 0%,#0f486c 68%,#145b7d 100%)',

          color:
            '#fff',
        }}
      >
        <div
          style={{
            display:
              'flex',

            alignItems:
              'flex-start',

            justifyContent:
              'space-between',

            gap:
              12,

            flexWrap:
              'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize:
                  11,

                fontWeight:
                  900,

                letterSpacing:
                  '.12em',

                color:
                  'rgba(255,255,255,.65)',
              }}
            >
              ADMIN CONSOLE
            </div>

            <h2
              style={{
                margin:
                  '4px 0 0',

                fontSize:
                  22,

                lineHeight:
                  1.2,

                letterSpacing:
                  '-.035em',
              }}
            >
              고객 가입·구독 현황
            </h2>

            <p
              style={{
                margin:
                  '7px 0 0',

                color:
                  'rgba(255,255,255,.72)',

                fontSize:
                  12,

                lineHeight:
                  1.6,
              }}
            >
              가입 고객, 플랜, 결제 상태와 텔레그램 연동 여부를 통합 조회합니다.
            </p>
          </div>

          <button
            type="button"
            onClick={
              refresh
            }
            disabled={
              loading
            }
            style={{
              minHeight:
                38,

              padding:
                '0 14px',

              border:
                '1px solid rgba(255,255,255,.35)',

              borderRadius:
                9,

              background:
                'rgba(255,255,255,.08)',

              color:
                '#fff',

              fontWeight:
                900,

              cursor:
                loading
                  ? 'wait'
                  : 'pointer',
            }}
          >
            {loading
              ? '조회 중…'
              : '새로고침'}
          </button>
        </div>

        <div
          style={{
            marginTop:
              16,

            display:
              'grid',

            gridTemplateColumns:
              'repeat(3,minmax(0,1fr))',

            gap:
              8,
          }}
        >
          <div
            style={
              summaryCardStyle
            }
          >
            <span
              style={
                summaryLabelStyle
              }
            >
              검색 결과
            </span>

            <strong
              style={
                summaryValueStyle
              }
            >
              {total.toLocaleString()}
              명
            </strong>
          </div>

          <div
            style={
              summaryCardStyle
            }
          >
            <span
              style={
                summaryLabelStyle
              }
            >
              현재 페이지
            </span>

            <strong
              style={
                summaryValueStyle
              }
            >
              {page} / {pageCount}
            </strong>
          </div>

          <div
            style={
              summaryCardStyle
            }
          >
            <span
              style={
                summaryLabelStyle
              }
            >
              페이지 크기
            </span>

            <strong
              style={
                summaryValueStyle
              }
            >
              {size}명
            </strong>
          </div>
        </div>
      </header>

      <div
        style={{
          padding:
            16,
        }}
      >
        <div
          style={{
            display:
              'grid',

            gridTemplateColumns:
              'minmax(220px,1fr) 170px auto',

            gap:
              8,

            alignItems:
              'center',
          }}
        >
          <input
            value={
              qInput
            }
            onChange={(
              event,
            ) =>
              setQInput(
                event.target.value,
              )
            }
            onKeyDown={(
              event,
            ) => {
              if (
                event.key === 'Enter'
              ) {
                event.preventDefault();
                submitSearch();
              }
            }}
            placeholder="이메일 또는 사용자 ID 검색"
            aria-label="고객 검색"
            style={
              controlStyle
            }
          />

          <select
            value={
              query.status
            }
            onChange={(
              event,
            ) => {
              request({
                status:
                  event.target
                    .value as AdminUsersStatusFilter,

                page:
                  1,
              });
            }}
            aria-label="구독 상태"
            style={
              controlStyle
            }
          >
            <option value="all">
              전체 상태
            </option>

            <option value="active">
              활성
            </option>

            <option value="past_due">
              결제 이슈
            </option>

            <option value="canceled">
              해지
            </option>
          </select>

          <div
            style={{
              justifySelf:
                'end',

              color:
                '#68798a',

              fontSize:
                12,

              fontWeight:
                800,
            }}
          >
            총{' '}
            {total.toLocaleString()}
            명
          </div>
        </div>

        {error ? (
          <div
            role="alert"
            style={{
              marginTop:
                10,

              padding:
                '9px 11px',

              border:
                '1px solid #efc0c0',

              borderRadius:
                9,

              background:
                '#fff5f5',

              color:
                '#a52b2b',

              fontSize:
                12,

              fontWeight:
                800,
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            marginTop:
              12,

            overflowX:
              'auto',

            border:
              '1px solid #dfe7ee',

            borderRadius:
              12,
          }}
        >
          <div
            style={{
              minWidth:
                980,
            }}
          >
            <div
              style={{
                ...rowGridStyle,

                padding:
                  '10px 12px',

                background:
                  '#f5f8fb',

                borderBottom:
                  '1px solid #dfe7ee',

                color:
                  '#718295',

                fontSize:
                  11,

                fontWeight:
                  900,
              }}
            >
              <div>
                USER ID
              </div>

              <div>
                이메일
              </div>

              <div>
                가입일
              </div>

              <div>
                권한
              </div>

              <div>
                플랜
              </div>

              <div>
                구독 상태
              </div>

              <div>
                텔레그램
              </div>
            </div>

            {!loaded &&
            loading ? (
              <div
                style={
                  messageStyle
                }
              >
                고객 정보를 불러오는 중입니다.
              </div>
            ) : items.length ? (
              items.map(
                (
                  row,
                ) => {
                  const tone =
                    statusTone(
                      row.subscriptionStatus,
                    );

                  return (
                    <div
                      key={String(
                        row.userId,
                      )}
                      style={{
                        ...rowGridStyle,

                        minHeight:
                          54,

                        padding:
                          '10px 12px',

                        borderBottom:
                          '1px solid #edf2f6',

                        alignItems:
                          'center',

                        color:
                          '#24384c',

                        opacity:
                          loading
                            ? 0.7
                            : 1,
                      }}
                    >
                      <div
                        style={{
                          fontFamily:
                            'monospace',

                          fontSize:
                            12,
                        }}
                      >
                        {row.userId}
                      </div>

                      <div
                        title={
                          row.email ||
                          row.emailMasked
                        }
                        style={{
                          minWidth:
                            0,

                          overflow:
                            'hidden',

                          textOverflow:
                            'ellipsis',

                          whiteSpace:
                            'nowrap',

                          fontSize:
                            12.5,

                          fontWeight:
                            850,
                        }}
                      >
                        {row.emailMasked ||
                          row.email ||
                          '-'}
                      </div>

                      <div
                        style={
                          subTextStyle
                        }
                      >
                        {formatDateTime(
                          row.createdAt,
                        )}
                      </div>

                      <div
                        style={{
                          fontSize:
                            12,

                          fontWeight:
                            850,
                        }}
                      >
                        {row.role ||
                          'USER'}
                      </div>

                      <div
                        style={{
                          fontSize:
                            12,

                          fontWeight:
                            900,
                        }}
                      >
                        {row.plan ||
                          'FREE'}
                      </div>

                      <div>
                        <span
                          style={
                            badgeStyle(
                              tone,
                            )
                          }
                        >
                          <i
                            style={
                              badgeDotStyle(
                                tone,
                              )
                            }
                          />

                          {statusLabel(
                            row.subscriptionStatus,
                          )}
                        </span>

                        {row.nextBillingDate ? (
                          <div
                            style={{
                              ...subTextStyle,

                              marginTop:
                                4,
                            }}
                          >
                            다음 결제{' '}
                            {formatDateTime(
                              row.nextBillingDate,
                            )}
                          </div>
                        ) : null}
                      </div>

                      <div
                        style={{
                          color:
                            row.telegramLinked
                              ? '#216b32'
                              : '#7a8998',

                          fontSize:
                            12,

                          fontWeight:
                            900,
                        }}
                      >
                        {row.telegramLinked
                          ? '연동'
                          : '미연동'}
                      </div>
                    </div>
                  );
                },
              )
            ) : (
              <div
                style={
                  messageStyle
                }
              >
                {error
                  ? '기존 조회 데이터가 없습니다.'
                  : '검색 결과가 없습니다.'}
              </div>
            )}
          </div>
        </div>

        <footer
          style={{
            marginTop:
              12,

            display:
              'flex',

            alignItems:
              'center',

            justifyContent:
              'space-between',

            gap:
              10,

            flexWrap:
              'wrap',
          }}
        >
          <div
            style={{
              color:
                '#718295',

              fontSize:
                12,
            }}
          >
            {page} / {pageCount}
            {' '}페이지
          </div>

          <div
            style={{
              display:
                'flex',

              gap:
                7,
            }}
          >
            <button
              type="button"
              disabled={
                page <= 1 ||
                loading
              }
              onClick={() =>
                request({
                  page:
                    Math.max(
                      1,
                      page - 1,
                    ),
                })
              }
              style={
                pagerButtonStyle
              }
            >
              이전
            </button>

            <button
              type="button"
              disabled={
                page >=
                  pageCount ||
                loading
              }
              onClick={() =>
                request({
                  page:
                    Math.min(
                      pageCount,
                      page + 1,
                    ),
                })
              }
              style={
                pagerButtonStyle
              }
            >
              다음
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
}

const summaryCardStyle:
  React.CSSProperties = {
    minWidth:
      0,

    padding:
      '9px 10px',

    border:
      '1px solid rgba(255,255,255,.14)',

    borderRadius:
      10,

    background:
      'rgba(255,255,255,.075)',
  };

const summaryLabelStyle:
  React.CSSProperties = {
    display:
      'block',

    color:
      'rgba(255,255,255,.62)',

    fontSize:
      10,

    fontWeight:
      900,
  };

const summaryValueStyle:
  React.CSSProperties = {
    display:
      'block',

    marginTop:
      2,

    color:
      '#fff',

    fontSize:
      16,

    fontWeight:
      950,
  };

const controlStyle:
  React.CSSProperties = {
    width:
      '100%',

    minHeight:
      40,

    boxSizing:
      'border-box',

    padding:
      '0 11px',

    border:
      '1px solid #cbd8e3',

    borderRadius:
      9,

    background:
      '#fff',

    color:
      '#21364a',

    fontSize:
      12.5,

    fontFamily:
      'inherit',

    outline:
      'none',
  };

const rowGridStyle:
  React.CSSProperties = {
    display:
      'grid',

    gridTemplateColumns:
      '100px minmax(210px,1.4fr) 150px 90px 90px 180px 90px',

    gap:
      10,
  };

const messageStyle:
  React.CSSProperties = {
    padding:
      28,

    textAlign:
      'center',

    color:
      '#6f8091',

    fontSize:
      12,
  };

const subTextStyle:
  React.CSSProperties = {
    color:
      '#718295',

    fontSize:
      11.5,

    lineHeight:
      1.45,
  };

const pagerButtonStyle:
  React.CSSProperties = {
    minHeight:
      34,

    padding:
      '0 13px',

    border:
      '1px solid #c8d5e0',

    borderRadius:
      8,

    background:
      '#fff',

    color:
      '#24384c',

    fontWeight:
      900,

    cursor:
      'pointer',
  };

function badgeStyle(
  tone:
    | 'ok'
    | 'warn'
    | 'danger'
    | 'neutral',
): React.CSSProperties {
  const palette = {
    ok: {
      background:
        '#edf8ef',

      border:
        '1px solid #b8dfc0',

      color:
        '#216b32',
    },

    warn: {
      background:
        '#fff7e6',

      border:
        '1px solid #ecd08f',

      color:
        '#8a5a00',
    },

    danger: {
      background:
        '#fff1f1',

      border:
        '1px solid #efc0c0',

      color:
        '#a52b2b',
    },

    neutral: {
      background:
        '#f2f5f7',

      border:
        '1px solid #d8e0e7',

      color:
        '#596b7d',
    },
  } as const;

  return {
    display:
      'inline-flex',

    alignItems:
      'center',

    gap:
      5,

    minHeight:
      23,

    padding:
      '0 8px',

    borderRadius:
      999,

    fontSize:
      11,

    fontWeight:
      900,

    ...palette[tone],
  };
}

function badgeDotStyle(
  tone:
    | 'ok'
    | 'warn'
    | 'danger'
    | 'neutral',
): React.CSSProperties {
  const color = {
    ok:
      '#2e8b43',

    warn:
      '#c58a18',

    danger:
      '#cb3d3d',

    neutral:
      '#8a98a6',
  }[tone];

  return {
    width:
      6,

    height:
      6,

    borderRadius:
      '50%',

    background:
      color,
  };
}
